import { showError, showHttpError } from '@calendly/alert'

import { setLocalStorageAlert } from 'hooks/use_local_storage_alerts'
import { rootApi } from 'root-api'
import { baseApiPath, memberPath } from 'services/event_type_path_utils'
import { extractErrorMessage } from 'shared/utils'

/**
 * Handles error by extracting message and showing it
 * @param {Object} error - The error object
 * @param {Function} dispatch - Redux dispatch function
 */
const handleError = (error, dispatch) => {
  const message = extractErrorMessage(error)
  if (message) {
    dispatch(showError(message))
    setLocalStorageAlert(message)
  } else {
    // Fallback to showHttpError if we can't extract a message
    dispatch(showHttpError(error))
  }
}

const updateSingleUseLinksCacheData = ({ api, state, dispatch, data }) => {
  api.util.selectInvalidatedBy(state, ['SINGLE-USE-LINKS']).forEach(({ endpointName, originalArgs }) => {
    if (endpointName === 'fetchSingleUseLinkData') {
      dispatch(
        api.util.updateQueryData('fetchSingleUseLinkData', originalArgs, (draft) => {
          if (draft?.results) {
            const existingIndex = draft.results.findIndex((item) => item.id === data.id)
            if (existingIndex >= 0) {
              draft.results[existingIndex] = data
            } else {
              draft.results.unshift(data)
            }
          }
        })
      )
    }
  })
}

const api = rootApi.enhanceEndpoints({ addTagTypes: [
  'EVENT_TYPES',
  'SINGLE-USE-LINKS'
] }).injectEndpoints({
  endpoints: (build) => ({
    fetchNewEventType: build.query({
      query: ({ type, kind, poolingType, ownerType, ownerId, selectedMemberIds }) => ({
        url: `${baseApiPath({ type, ownerType, ownerId })}/new`,
        params: {
          type,
          kind,
          pooling_type: poolingType,
          ...(selectedMemberIds ? { selected_member_ids: selectedMemberIds } : {})
        }
      }),
      extraOptions: {
        // When we get everything ported over to RTK query, we can remove this,
        // but for now, the camelCase breaks something
        transformRequest: false,
        transformResponse: false,
        handleErrors: false // We handle errors in onQueryStarted to extract the correct message from policy.base
      },
      onQueryStarted: async ({ onSuccess, onFailure }, { queryFulfilled, dispatch }) => {
        try {
          const { data: response } = await queryFulfilled

          if (onSuccess) { onSuccess(response) }
        } catch (e) {
          handleError(e.error, dispatch)
          if (onFailure) { onFailure() }
        }
      }
    }),
    fetchEventType: build.query({
      query: (eventType) => ({
        url: memberPath(eventType)
      }),
      // DO NOT REMOVE this is needed because when user switch between event types
      // user can get old cached data when opening once opened Event Type and get the old data instead of fresh
      keepUnusedDataFor: 0,
      extraOptions: {
        // When we get everything ported over to RTK query, we can remove this,
        // but for now, the camelCase breaks something
        transformRequest: false,
        transformResponse: false,
        handleErrors: false // We handle errors in onQueryStarted to extract the correct message from policy.base
      },
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled
        } catch (e) {
          handleError(e.error, dispatch)
        }
      },
      providesTags: (_result, _error, { id }) => [
        { type: 'EVENT_TYPES', id }
      ]
    }),
    fetchSingleUseLink: build.query({
      query: ({ shareUuid }) => ({
        url: `/api/shares/${shareUuid}`,
      }),
      keepUnusedDataFor: 0,
      providesTags: ({ id }, _error) => [{ type: 'SINGLE-USE-LINKS', id }],
      extraOptions: {
        handleErrors: false // We handle errors in onQueryStarted to extract the correct message from policy.base
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          const state = getState()

          if (data) {
            updateSingleUseLinksCacheData({ api, state, dispatch, data })
          }
        } catch (e) {
          handleError(e.error, dispatch)
        }
      }
    }),
  })
})

export const {
  useFetchEventTypeQuery,
  useFetchNewEventTypeQuery,
  useFetchSingleUseLinkQuery,
  useLazyFetchEventTypeQuery,
  useLazyFetchNewEventTypeQuery,
} = api
